import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface SpvMbData {
    users_estate: string;
    estate: string;
    job_title: string | null;
    nip: string;
    name: string;
    division: string | null;
    block: string;
    spv_name: string | null;
    job_level: string | null;
    penalty_code: string | null;
    penalty_descr: string | null;
    qty: number | null;
    inspection_date: string | null;
    no_tph: string | null;
    no_baris: string | null;
    no_pokok: string | null;
    ins_description: string | null;
    inspection_date_mb: string | null;
    spv_name_mb: string | null;
    job_level_mb: string | null;
    block_mb: string | null;
    tph_code: string | null;
    unripe_qty: number;
    lstalk_qty: number;
    mb_description: string | null;
    foreman_name: string | null;
    kerani_harvest_name: string | null;
    foreman1_name: string | null;
    asisten_name: string | null;
    asisten_kepala_name: string | null;
    estate_manager: string | null;
}

interface ReportSpvMbProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    months: Array<{ value: string; label: string }>;
    toast: any;
}

const ReportSpvMb = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, months, toast }: ReportSpvMbProps) => {
        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }

            const response = await api.get<SpvMbData[]>(
                '/api/inspection/report-spv-mb',
                { params }
            );

            if (!response.data || response.data.length === 0) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk dilaporkan pada periode terpilih.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            const reportData: SpvMbData[] = response.data;

            // Kelompokkan data berdasarkan estate
            const groupedByEstate = reportData.reduce((acc, item) => {
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!acc[estateKey]) {
                    acc[estateKey] = [];
                }
                acc[estateKey].push(item);
                return acc;
            }, {} as Record<string, SpvMbData[]>);

            const wb = XLSX.utils.book_new();

            const columnOrder = [
                'NAMA KARYAWAN',
                'NIP',
                'JABATAN',
                'MANDOR PANEN',
                'KERANI PANEN',
                'MANDOR 1',
                'ASISTEN',
                'ASISTEN KEPALA',
                'MANAGER',
                'DIVISI',
                'BLOK',
                'TANGGAL INSPEKSI',
                'NAMA SUPERVISOR',
                'LEVEL SUPERVISOR',
                'NAMA SUPERVISOR MUTU BUAH',
                'LEVEL SUPERVISOR MUTU BUAH',
                'KODE PENALTY',
                'DESKRIPSI PENALTY',
                'PENALTY MUTU BUAH',
                'QTY',
                'MENTAH QTY',
                'TANGKAI PANJANG QTY',
                'NO TPH',
                'NO BARIS',
                'NO POKOK',
                'DESKRIPSI TEMUAN',
                'DESKRIPSI MUTU BUAH',
            ];

            const headerStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const cellBorderStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const centerAlignment = { alignment: { horizontal: "center", vertical: "center" } };
            const leftAlignment = { alignment: { horizontal: "left", vertical: "center" } };
            const rightAlignment = { alignment: { horizontal: "right", vertical: "center" } };

            for (const estateName in groupedByEstate) {
                let inspectionsForEstate = groupedByEstate[estateName];

                // Urutkan berdasarkan nama karyawan
                inspectionsForEstate.sort((a, b) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    // Jika nama sama, sort by tanggal inspeksi
                    if (a.inspection_date && b.inspection_date) {
                        return new Date(a.inspection_date).getTime() - new Date(b.inspection_date).getTime();
                    }
                    return 0;
                });

                const sheetData = inspectionsForEstate.map(item => {
                    // Supervisor utama
                    const supervisorName = item.spv_name || '-';
                    const supervisorLevel = item.job_level || '-';
                    // Supervisor mutu buah
                    const supervisorMbName = item.spv_name_mb || '-';
                    const supervisorMbLevel = item.job_level_mb || '-';
                    // Gabungkan blok dari kedua sumber
                    const blockName = item.block || item.block_mb || '-';
                    // Gabungkan NO TPH dari kedua sumber
                    let tphValue = '-';
                    if (item.no_tph && item.tph_code) {
                        tphValue = `${item.no_tph} - ${item.tph_code}`;
                    } else if (item.no_tph) {
                        tphValue = item.no_tph;
                    } else if (item.tph_code) {
                        tphValue = item.tph_code;
                    }
                    // Tentukan penalty mutu buah
                    let penaltyMutuBuah = '';
                    if ((item.unripe_qty && item.unripe_qty > 0) && (item.lstalk_qty && item.lstalk_qty > 0)) {
                        penaltyMutuBuah = 'Mentah, Tangkai Panjang';
                    } else if (item.unripe_qty && item.unripe_qty > 0) {
                        penaltyMutuBuah = 'Mentah';
                    } else if (item.lstalk_qty && item.lstalk_qty > 0) {
                        penaltyMutuBuah = 'Tangkai Panjang';
                    } else {
                        penaltyMutuBuah = '-';
                    }

                    return {
                        'NAMA KARYAWAN': item.name || '-',
                        'NIP': item.nip ?? '-',
                        'JABATAN': item.job_title ?? '-',
                        'MANDOR PANEN': item.foreman_name ?? '-',
                        'KERANI PANEN': item.kerani_harvest_name ?? '-',
                        'MANDOR 1': item.foreman1_name ?? '-',
                        'ASISTEN': item.asisten_name ?? '-',
                        'ASISTEN KEPALA': item.asisten_kepala_name ?? '-',
                        'MANAGER': item.estate_manager ?? '-',
                        'DIVISI': item.division ?? '-',
                        'BLOK': blockName,
                        'TANGGAL INSPEKSI': item.inspection_date || '-',
                        'NAMA SUPERVISOR': supervisorName,
                        'LEVEL SUPERVISOR': supervisorLevel,
                        'NAMA SUPERVISOR MUTU BUAH': supervisorMbName,
                        'LEVEL SUPERVISOR MUTU BUAH': supervisorMbLevel,
                        'KODE PENALTY': item.penalty_code ?? '-',
                        'DESKRIPSI PENALTY': item.penalty_descr ?? '-',
                        'PENALTY MUTU BUAH': penaltyMutuBuah,
                        'QTY': item.qty === 0 ? '-' : (item.qty ?? '-'),
                        'MENTAH QTY': (item.unripe_qty === 0 || item.unripe_qty === null) ? '-' : item.unripe_qty,
                        'TANGKAI PANJANG QTY': (item.lstalk_qty === 0 || item.lstalk_qty === null) ? '-' : item.lstalk_qty,
                        'NO TPH': tphValue,
                        'NO BARIS': item.no_baris ?? '-',
                        'NO POKOK': item.no_pokok ?? '-',
                        'DESKRIPSI TEMUAN': item.ins_description ?? '-',
                        'DESKRIPSI MUTU BUAH': item.mb_description ?? '-',
                    };
                });

                const ws = XLSX.utils.json_to_sheet([], { header: columnOrder });
                XLSX.utils.sheet_add_json(ws, sheetData, { skipHeader: true, origin: 'A2' });

                // Styling Header
                for (let C = 0; C < columnOrder.length; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!ws[cellAddress]) ws[cellAddress] = { v: columnOrder[C] };
                    else ws[cellAddress].v = columnOrder[C];
                    ws[cellAddress].s = headerStyle;
                }

                // Styling Data Rows
                sheetData.forEach((rowData, rowIndex) => {
                    // Check if this row has mutu buah issues (for yellow highlighting)
                    const hasMutuBuahIssue = rowData['MENTAH QTY'] !== '-' || rowData['TANGKAI PANJANG QTY'] !== '-';

                    columnOrder.forEach((colName, colIndex) => {
                        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });

                        // Ensure cell exists even if empty
                        if (!ws[cellAddress]) {
                            ws[cellAddress] = { v: (rowData as any)[colName] || '-' };
                        }

                        if (ws[cellAddress]) {
                            ws[cellAddress].s = { ...cellBorderStyle };

                            // Add yellow background for rows with mutu buah issues
                            if (hasMutuBuahIssue) {
                                ws[cellAddress].s.fill = {
                                    type: "pattern",
                                    pattern: "solid",
                                    fgColor: { rgb: "FFFF99" } // Light yellow
                                };
                            }

                            // Specific alignments
                            if (['NAMA KARYAWAN', 'NIP', 'JABATAN', 'MANDOR PANEN', 'KERANI PANEN', 'MANDOR 1', 'ASISTEN', 'ASISTEN KEPALA', 'MANAGER', 'DIVISI', 'BLOK', 'NAMA SUPERVISOR', 'LEVEL SUPERVISOR', 'KODE PENALTY', 'DESKRIPSI PENALTY', 'DESKRIPSI TEMUAN', 'PENALTY MUTU BUAH', 'DESKRIPSI MUTU BUAH'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...leftAlignment };
                            } else if (['TANGGAL INSPEKSI', 'NO TPH', 'NO BARIS', 'NO POKOK'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...centerAlignment };
                            } else if (['QTY', 'MENTAH QTY', 'TANGKAI PANJANG QTY'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...rightAlignment };

                                // Format angka dan styling untuk nilai "-"
                                if (ws[cellAddress].v === '-') {
                                    ws[cellAddress].s.font = { italic: true, color: { rgb: "808080" } };
                                } else if (typeof ws[cellAddress].v === 'number') {
                                    ws[cellAddress].t = 'n';
                                    ws[cellAddress].z = '#,##0';
                                }
                            }

                            // Format tanggal
                            if (colName === 'TANGGAL INSPEKSI' && ws[cellAddress].v !== '-') {
                                ws[cellAddress].t = 's'; // Set as string to avoid date conversion issues
                                // Convert date format from YYYY-MM-DD to DD/MM/YYYY
                                const dateValue = ws[cellAddress].v;
                                if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                    const [year, month, day] = dateValue.split('-');
                                    ws[cellAddress].v = `${day}/${month}/${year}`;
                                }
                            }
                        }
                    });
                });

                // Ensure all cells have borders - additional pass to fix any missing borders
                for (let row = 1; row <= sheetData.length + 1; row++) {
                    for (let col = 0; col < columnOrder.length; col++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                        if (!ws[cellAddress]) {
                            ws[cellAddress] = { v: row === 1 ? columnOrder[col] : '-' };
                        }
                        if (!ws[cellAddress].s) {
                            ws[cellAddress].s = {};
                        }
                        ws[cellAddress].s.border = {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        };
                    }
                }

                // Column Widths
                ws['!cols'] = [
                    { wch: 25 }, // NAMA KARYAWAN
                    { wch: 15 }, // NIP
                    { wch: 20 }, // JABATAN
                    { wch: 25 }, // MANDOR PANEN
                    { wch: 25 }, // KERANI PANEN
                    { wch: 25 }, // MANDOR 1
                    { wch: 25 }, // ASISTEN
                    { wch: 25 }, // ASISTEN KEPALA
                    { wch: 25 }, // MANAGER
                    { wch: 20 }, // DIVISI
                    { wch: 15 }, // BLOK
                    { wch: 15 }, // TANGGAL INSPEKSI
                    { wch: 25 }, // NAMA SUPERVISOR
                    { wch: 20 }, // LEVEL SUPERVISOR
                    { wch: 25 }, // NAMA SUPERVISOR MUTU BUAH
                    { wch: 25 }, // LEVEL SUPERVISOR MUTU BUAH
                    { wch: 18 }, // KODE PENALTY
                    { wch: 84 }, // DESKRIPSI PENALTY
                    { wch: 25 }, // PENALTY MUTU BUAH
                    { wch: 8 },  // QTY
                    { wch: 12 }, // MENTAH QTY
                    { wch: 18 }, // TANGKAI PANJANG QTY
                    { wch: 20 }, // NO TPH
                    { wch: 15 }, // NO BARIS
                    { wch: 15 }, // NO POKOK
                    { wch: 40 }, // DESKRIPSI TEMUAN
                    { wch: 40 }, // DESKRIPSI MUTU BUAH
                ];

                ws['!rows'] = [{ hpt: 25 }]; // Tinggi baris header

                // Nama sheet yang aman
                const safeSheetName = estateName.replace(/[\\/*?[\]:]/g, "").substring(0, 30);
                XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
            }

            const selectedMonthLabel = months.find(m => m.value === selectedMonth)?.label ?? selectedMonth;
            XLSX.writeFile(wb, `Laporan Supervisi-Mutu Buah ${selectedMonthLabel}.xlsx`);
            trackDownload('Laporan Supervisi-Mutu Buah', { month: selectedMonthLabel });

            toast({
                title: 'Success',
                description: 'Laporan supervisi-mutu buah berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error exporting spv-mb report:', error);
            let description = 'Gagal mengekspor laporan supervisi-mutu buah';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message ?? error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default ReportSpvMb;
